/**
 * GSAP-полёт карты в центр взятки для онлайна — траектория 1:1 с
 * {@link ../../belot/hooks/useBelotArmenianAnimations}; вместо `apply(cmd)` вызывается `onSend(payload)`.
 * Звуки хода остаются на `useBelotOnlineFxBus` по дифу снапшота.
 *
 * @module components/online/hooks/useBelotOnlinePlayCardFlight
 */

import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/** Как `CARD_PLAY_FLIGHT_SEC` в `BelotArmenianLocalLobby`. */
export const ONLINE_BELOT_CARD_PLAY_FLIGHT_SEC = 0.46;

/**
 * @param {object} args
 * @param {object|null} args.cardFly — { key, x0, y0, x1, y1, rank, suit, fromDrag? }
 * @param {(v: object|null) => void} args.setCardFly
 * @param {React.MutableRefObject<HTMLElement|null>} args.flyCardWrapRef
 * @param {React.MutableRefObject<import('gsap').core.Timeline|null>} args.flyTweenRef
 * @param {React.MutableRefObject<HTMLElement|null>} args.trickCenterRef
 * @param {React.MutableRefObject<object|null>} args.pendingPayloadRef — payload для `PLAY_CARD`
 * @param {(payload: object) => void} args.onSend
 * @param {() => void} [args.onFlightStart]
 * @param {() => void} [args.onFlightEnd]
 * @param {number} [args.cardPlayFlightSec]
 * @param {() => Promise<void>} [args.waitUntilFxIdle] — дождаться пустой очереди FX перед стартом GSAP.
 */
export function useBelotOnlinePlayCardFlight({
  cardFly,
  setCardFly,
  flyCardWrapRef,
  flyTweenRef,
  trickCenterRef,
  pendingPayloadRef,
  onSend,
  onFlightStart,
  onFlightEnd,
  cardPlayFlightSec = ONLINE_BELOT_CARD_PLAY_FLIGHT_SEC,
  waitUntilFxIdle,
}) {
  const onSendRef = useRef(onSend);
  const onFlightStartRef = useRef(onFlightStart);
  const onFlightEndRef = useRef(onFlightEnd);
  onSendRef.current = onSend;
  onFlightStartRef.current = onFlightStart;
  onFlightEndRef.current = onFlightEnd;
  const waitUntilFxIdleRef = useRef(waitUntilFxIdle);
  waitUntilFxIdleRef.current = waitUntilFxIdle;

  useLayoutEffect(() => {
    if (!cardFly) return undefined;
    let cancelled = false;
    flyTweenRef.current?.kill();
    flyTweenRef.current = null;

    const startTween = () => {
      if (cancelled) return;
      const el = flyCardWrapRef.current;
      const payload = pendingPayloadRef.current;
      const cf = cardFly;
      if (!el || !payload || !cf) {
        pendingPayloadRef.current = null;
        queueMicrotask(() => setCardFly(null));
        onFlightEndRef.current?.();
        return;
      }
      onFlightStartRef.current?.();
    const dx = cf.x1 - cf.x0;
    const dy = cf.y1 - cf.y0;
    const dist = Math.hypot(dx, dy);
    const fromDrag = Boolean(cf.fromDrag);
    const signX = dx === 0 ? 1 : Math.sign(dx);
    const arcLift = clamp(dist * 0.1, 28, 72);
    const c1x = cf.x0 + dx * 0.22;
    const c1y = cf.y0 - arcLift * 0.7;
    const c2x = cf.x0 + dx * 0.62;
    const c2y = cf.y0 + dy * 0.46 - arcLift;
    const anticipationDur = fromDrag
      ? clamp(cardPlayFlightSec * 0.18, 0.07, 0.1)
      : clamp(cardPlayFlightSec * 0.28, 0.1, 0.14);
    const flightDur = fromDrag
      ? clamp(cardPlayFlightSec * 0.56, 0.2, 0.32)
      : clamp(cardPlayFlightSec * 0.72, 0.26, 0.42);
    const skewPeak = clamp((dist / 500) * signX * 5, -7, 7);
    const impactShadow = fromDrag ? '0 10px 18px rgba(0, 0, 0, 0.32)' : '0 14px 24px rgba(0, 0, 0, 0.42)';
    const centerEl = trickCenterRef.current;
    gsap.set(el, {
      left: cf.x0,
      top: cf.y0,
      xPercent: -50,
      yPercent: -50,
      rotation: signX * -10,
      scale: 0.9,
      skewX: 0,
      boxShadow: '0 0 0 rgba(0,0,0,0)',
      opacity: 1,
    });
    const tl = gsap.timeline({
      onComplete: () => {
        const p = pendingPayloadRef.current;
        pendingPayloadRef.current = null;
        setCardFly(null);
        if (p) {
          try {
            onSendRef.current(p);
          } catch {
            /* ignore */
          }
        }
        onFlightEndRef.current?.();
      },
    });
    tl.to(el, {
      duration: anticipationDur,
      ease: 'power1.out',
      y: fromDrag ? '-=6' : '-=10',
      scale: fromDrag ? 1.02 : 1.05,
      rotation: signX * (fromDrag ? 3 : 5),
      skewX: skewPeak * 0.4,
    });
    tl.to(el, {
      duration: flightDur,
      ease: 'power2.inOut',
      keyframes: [
        { left: c1x, top: c1y, rotation: signX * 8, skewX: skewPeak * 0.75, scale: 1.01 },
        { left: c2x, top: c2y, rotation: signX * 3, skewX: skewPeak, scale: 1 },
        {
          left: cf.x1,
          top: cf.y1,
          rotation: gsap.utils.random(-10, 10),
          skewX: skewPeak * 0.35,
          scale: 1,
        },
      ],
    });
    tl.to(
      el,
      {
        duration: 0.05,
        ease: 'power2.out',
        scale: fromDrag ? 1.01 : 1.02,
        boxShadow: impactShadow,
      },
      '>-0.02',
    );
    tl.to(el, {
      duration: 0.07,
      ease: 'power1.out',
      scale: 1,
      skewX: 0,
      boxShadow: '0 0 0 rgba(0,0,0,0)',
    });
    if (centerEl && !fromDrag) {
      tl.to(
        centerEl,
        {
          duration: 0.05,
          ease: 'power1.inOut',
          x: '+=1.4',
          y: '-=1.2',
          yoyo: true,
          repeat: 1,
        },
        '<',
      );
      tl.set(centerEl, { clearProps: 'x,y' });
    }
    flyTweenRef.current = tl;
    };

    void (async () => {
      if (typeof waitUntilFxIdleRef.current === 'function') {
        try {
          await waitUntilFxIdleRef.current();
        } catch {
          /* ignore */
        }
      }
      if (cancelled) return;
      startTween();
    })();

    return () => {
      cancelled = true;
      const interrupted = Boolean(pendingPayloadRef.current);
      flyTweenRef.current?.kill();
      flyTweenRef.current = null;
      if (interrupted) {
        pendingPayloadRef.current = null;
        queueMicrotask(() => setCardFly(null));
        onFlightEndRef.current?.();
      }
    };
  }, [cardFly, setCardFly, flyCardWrapRef, flyTweenRef, trickCenterRef, pendingPayloadRef, cardPlayFlightSec]);
}
